import * as Sentry from '@sentry/browser';

type Args<T> = {
  /**
   * Lets the request outlive the page (fetch `keepalive`), e.g. across a
   * full-page redirect. Omitted by default.
   */
  keepalive?: boolean;
  /** Aborts the request (fetch `signal`), e.g. `AbortSignal.timeout(ms)`. */
  signal?: AbortSignal;
} & (
  | {
      baseURL: string;
      path: string;
      token?: string;
      method: 'GET';
      body?: never;
      headers?: Record<string, string>;
      searchParams?: Record<
        string,
        string | boolean | number | undefined | string[]
      >;
    }
  | {
      baseURL: string;
      path: string;
      token?: string;
      method: 'POST' | 'PATCH' | 'PUT' | 'DELETE';
      body?: T;
      headers?: Record<string, string>;
      searchParams?: Record<
        string,
        string | boolean | number | undefined | string[]
      >;
    }
);
export const send = async <Body, Return = Body>({
  baseURL,
  path,
  token,
  method,
  body,
  headers,
  searchParams,
  keepalive,
  signal,
}: Args<Body>): Promise<Return> => {
  headers = {
    'Content-Type': 'application/json',
    ...(headers ? headers : ''),
  };
  const cleanSearchParamsObject = new URLSearchParams(
    Object.fromEntries(
      Object.entries(searchParams ?? {})
        .filter(([, value]) => value !== undefined)
        .map(([key, value]) => [key, value?.toString() ?? ''])
    )
  );
  const searchParamsString =
    cleanSearchParamsObject.size > 0 ? `?${cleanSearchParamsObject}` : '';
  const url = new URL(path, baseURL) + searchParamsString;

  if (token) headers['Authorization'] = token;
  try {
    const response = await fetch(url, {
      method,
      mode: 'cors',
      headers: headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      ...(keepalive ? { keepalive: true } : {}),
      ...(signal ? { signal } : {}),
    });
    if (!response.ok) {
      let responseBody: unknown = null;
      try {
        const responseText = await response.text();
        try {
          responseBody = JSON.parse(responseText);
        } catch {
          responseBody = responseText || null;
        }
      } catch {
        responseBody = null;
      }

      Sentry.setContext('apiErrorResponse', {
        status: response.status,
        method,
        url,
        body:
          typeof responseBody === 'string'
            ? responseBody.slice(0, 2000)
            : JSON.stringify(responseBody).slice(0, 2000),
      });

      if (response.status === 429) {
        console.warn(`Warning: Too many requests on ${method} ${path}.`);
        return undefined as Return;
      }

      const bodyRecord =
        responseBody && typeof responseBody === 'object'
          ? (responseBody as Record<string, unknown>)
          : undefined;
      const bodyMessage = bodyRecord?.['message'] ?? bodyRecord?.['Message'];
      const errorMessage =
        bodyMessage || response.statusText || 'Unknown error';
      throw new Error(`Error: ${response.status}: ${errorMessage}`);
    }
    const contentType = response.headers.get('Content-Type');
    if (
      contentType?.includes('application/pdf') ||
      contentType?.includes('image/')
    ) {
      return response.url as Return;
    }
    if (contentType?.includes('application/json')) {
      const text = await response.text();
      return (text.trim() ? JSON.parse(text) : undefined) as Return;
    }
    if (contentType?.includes('text/')) {
      return (await response.text()) as Return;
    }
    return undefined as Return;
  } catch (error) {
    if (error instanceof Error) {
      if (error.message.includes('429')) {
        console.warn(
          `Warning: Too many requests to ${method} ${path}. Muting error.`
        );
        return undefined as Return;
      }
      throw new Error(`Failed to ${method} ${path}; ${error?.message}`);
    }
    throw new Error(`Failed to ${method} ${path}`);
  }
};
